import React, { useCallback, useEffect, useState } from 'react';
import { IonLabel, IonSegment, IonSegmentButton, IonSpinner, IonToast } from '@ionic/react';
import { ServiceEstimate, ServiceSettingsBundle } from '../../../interfaces/ServiceEstimate';
import { serviceApi } from '../../../utils/serviceEstimatesApi';
import EstimateEditor from './EstimateEditor';
import SavedEstimates from './SavedEstimates';
import ServiceSettingsAdmin from './ServiceSettingsAdmin';
import './ServiceEstimates.css';

type Tab = 'estimate' | 'saved' | 'settings';

/**
 * Service estimates (Field Estimator), Phase 1.
 * Mount on a page inside the AE2 shell and add a "Service Estimates" menu item in RoutesList.tsx,
 * wrapped in CheckRole for the Service, Admin and Director roles.
 */
const ServiceEstimates: React.FC = () => {
  const [tab, setTab] = useState<Tab>('estimate');
  const [bundle, setBundle] = useState<ServiceSettingsBundle | null>(null);
  const [current, setCurrent] = useState<ServiceEstimate | null>(null);
  const [toast, setToast] = useState('');
  const [error, setError] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);

  const loadSettings = useCallback(() => {
    serviceApi.settings().then(setBundle).catch((e) => setError(e instanceof Error ? e.message : 'Could not load Service estimates.'));
  }, []);
  useEffect(loadSettings, [loadSettings]);

  if (error) return <div className="service-estimates"><p className="service-estimates-error">{error}</p></div>;
  if (!bundle) return <div className="service-estimates"><IonSpinner /></div>;

  return (
    <div className="service-estimates">
      <header className="service-estimates-head">
        <div>
          <h1>Service estimates</h1>
          <p>Quote mechanical service work on site, add the ACS# from the office, and have the customer sign.</p>
        </div>
        <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as Tab)} className="service-estimates-tabs">
          <IonSegmentButton value="estimate"><IonLabel>Estimate</IonLabel></IonSegmentButton>
          <IonSegmentButton value="saved"><IonLabel>Saved</IonLabel></IonSegmentButton>
          {bundle.can_admin && <IonSegmentButton value="settings"><IonLabel>Settings</IonLabel></IonSegmentButton>}
        </IonSegment>
      </header>

      {tab === 'estimate' && (
        <EstimateEditor
          bundle={bundle}
          estimate={current}
          onSaved={(e) => { setCurrent(e); setRefreshKey((k) => k + 1); }}
          onNew={() => setCurrent(null)}
          notify={setToast}
        />
      )}
      {tab === 'saved' && <SavedEstimates refreshKey={refreshKey} onOpen={(e) => { setCurrent(e); setTab('estimate'); }} />}
      {tab === 'settings' && bundle.can_admin && <ServiceSettingsAdmin bundle={bundle} onChanged={loadSettings} notify={setToast} />}

      <IonToast isOpen={!!toast} message={toast} duration={3000} onDidDismiss={() => setToast('')} position="bottom" />
    </div>
  );
};

export default ServiceEstimates;
